/**
 * The shape of the answer, while the answer is on its way.
 *
 * Cards rather than a spinner, and cards roughly the height of the real ones, so
 * the page does not resize under the reader when they land (D-LD4). This started
 * in the gallery, where the owner's listings tab had a placeholder grid and the
 * public grid put a bare sentence where the cards go — two panels of one page
 * loading in two different registers, side by side in the tab strip. It lives
 * here now because every network-backed surface has the same two panels.
 *
 * ## Two shapes, because there are two shapes of answer
 *
 * {@link SkeletonTiles} is for a grid of cards and {@link SkeletonRows} for a
 * list of rows: a title line and a line of metadata, in the same bordered idiom
 * the card uses. Which one {@link Skeleton} draws is decided by which count it
 * is given — `<Skeleton tiles={8} …>` or `<Skeleton rows={5} …>` — rather than
 * by a `kind` prop beside a `count`, because the call site then says the whole
 * thing in one word and the types make "both" and "neither" unwritable.
 *
 * {@link SkeletonBlock} is the third and it is not a list at all: one rectangle
 * where a chart, a figure or a map will be. It stands outside {@link Skeleton}
 * for that reason — a block is not an item, so wrapping it in the `<ul>` would
 * have meant a list of one, and the count union above says "tiles or rows"
 * honestly only while a block is not one of the answers. The surface that draws
 * a block writes the `sr-only` sentence itself, which is one line at the call
 * site against a third variant of the wrapper here.
 *
 * ## Two entry points, because there are two places an item goes
 *
 * {@link Skeleton} is the whole placeholder: a list of items standing in for a
 * list that is not there yet. The bare pair is the items alone, for the case
 * where the list *is* there — "Show more" appends to a real `<ul>`, and a second
 * list beside it would tell a screen reader the page had grown a new one. A
 * `bare` flag on one component would have been the same two shapes behind a
 * boolean, and the caller reads better naming which it wants.
 *
 * Either way the items are `aria-hidden` with a live region beside them (the
 * wrapper's, or the caller's): a screen reader is told "loading" once, rather
 * than read a paragraph of placeholder per item.
 */

import { cn } from '@/lib/utils';

/** Exactly one count, so a placeholder cannot ask for tiles *and* rows. */
type SkeletonShape =
  | { readonly tiles: number; readonly rows?: never }
  | { readonly rows: number; readonly tiles?: never };

/**
 * A list of placeholder items, and one sentence saying what it means.
 *
 * `className` is the layout the real content will use — passed in rather than
 * written here, so a placeholder cannot drift into a different number of
 * columns from what replaces it.
 */
export function Skeleton({
  label,
  className,
  testId,
  ...shape
}: SkeletonShape & {
  readonly label: string;
  readonly className: string;
  readonly testId: string;
}) {
  return (
    <>
      <p className="sr-only" role="status">
        {label}
      </p>
      <ul aria-hidden="true" className={className}>
        {shape.tiles === undefined ? (
          <SkeletonRows rows={shape.rows} testId={testId} />
        ) : (
          <SkeletonTiles tiles={shape.tiles} testId={testId} />
        )}
      </ul>
    </>
  );
}

/** The card tiles on their own, for appending to a grid that already exists. */
export function SkeletonTiles({
  tiles,
  testId,
}: {
  readonly tiles: number;
  readonly testId: string;
}) {
  return (
    <>
      {Array.from({ length: tiles }, (_unused, index) => (
        <li
          // biome-ignore lint/suspicious/noArrayIndexKey: a placeholder has no identity beyond its position.
          key={index}
          aria-hidden="true"
          data-testid={testId}
          className="flex h-full animate-pulse flex-col rounded-lg border border-border bg-card/40"
        >
          <div className="flex min-h-24 flex-1 flex-col gap-2.5 px-4 py-3">
            <div className="flex flex-col gap-1.5">
              <div className="h-4 w-40 max-w-full rounded bg-muted" />
              <div className="h-3 w-28 max-w-full rounded bg-muted/70" />
            </div>
            <div className="mt-auto h-3 w-48 max-w-full rounded bg-muted/70" />
          </div>
          <div className="border-t border-border px-4 py-2">
            <div className="h-3 w-32 max-w-full rounded bg-muted/70" />
          </div>
        </li>
      ))}
    </>
  );
}

/**
 * The list rows on their own, for appending to a list that already exists.
 *
 * Two lines, because that is what a row of this app is: what it is called, and
 * one line of detail under it. The widths are fractions rather than fixed, so
 * one row shape serves a dialog's narrow list and a settings page's wide one
 * without either looking like a different component mid-load.
 */
export function SkeletonRows({ rows, testId }: { readonly rows: number; readonly testId: string }) {
  return (
    <>
      {Array.from({ length: rows }, (_unused, index) => (
        <li
          // biome-ignore lint/suspicious/noArrayIndexKey: a placeholder has no identity beyond its position.
          key={index}
          aria-hidden="true"
          data-testid={testId}
          className="flex animate-pulse flex-col gap-2 rounded-lg border border-border bg-card/40 px-4 py-3"
        >
          <div className="h-4 w-2/5 rounded bg-muted" />
          <div className="h-3 w-1/4 rounded bg-muted/70" />
        </li>
      ))}
    </>
  );
}

/**
 * One block, where something that is not a list will be drawn.
 *
 * Nothing inside it: a chart has no rows to imitate, and a placeholder with
 * fake axes on it would be a picture of data nobody has yet. What it has
 * instead is the *height* the caller gives it, which is the whole job — a panel
 * that reserved the chart's height does not grow under the reader when the
 * chart lands (D-LD4), and that height is only knowable at the call site.
 *
 * `aria-hidden`, like the items above, because the surface drawing it owns the
 * one spoken sentence.
 */
export function SkeletonBlock({
  className,
  testId,
}: {
  readonly className: string;
  readonly testId: string;
}) {
  return (
    <div
      aria-hidden="true"
      data-testid={testId}
      className={cn('animate-pulse rounded-lg border border-border bg-card/40', className)}
    />
  );
}
